import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useElements,
  useStripe,
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { ToastMessage } from '@utility/toast';
import {
  elementsOptions,
  ErrorMessageConstant,
} from '@utility/constants/common';
import { useState } from 'react';
import {
  ControlledTextFormField,
  FieldRequiredLabel,
} from '@elements/FormField';
import { MuteText } from '@elements/Common';
import { AppModalXIcon } from '@elements/Modal';
import { useStripeOptions } from '@hooks/stripe';
import { StyledButton, BorderButton } from '@elements/Button';
import WalletService from '@utility/services/wallet';
import { getStripe } from '@utility/stripe-load';
import { Plus } from '@phosphor-icons/react';
import { LoaderBar } from './LoaderBar';
import { PaymentDialog } from './PaymentDialog';

const AddCreditCardView = {
  message: 0,
  addCreditCard: 1,
  success: 2,
};

/* Stripe draws each card field in its own iframe; this frames it like an input. */
const STRIPE_FIELD =
  'sk:rounded-full sk:bg-surface sk:px-3 sk:py-1.5 sk:inset-shadow-hairline sk:shadow-card';

const HEADING = 'sk:text-lg sk:font-semibold sk:leading-snug';

const CardSchema = yup.object().shape({
  name: yup.string().trim().required(),
});

function AddCreditCard({ close }) {
  const stripe = useStripe();
  const options = useStripeOptions();
  const elements = useElements();
  const [completedCardNumber, setCardNumberCompleted] = useState(false);
  const [completedCardExpiry, setCardExpiryCompleted] = useState(false);
  const [completedCardCVC, setCardCVCCompleted] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(CardSchema),
    shouldUnregister: true,
  });
  const [saving, setSaving] = useState('');

  const addCard = async data => {
    try {
      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return;
      }
      setSaving('Saving');
      const card = elements.getElement(CardNumberElement);
      // @ts-expect-error - TS2769: No overload matches this call.
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          name: data.name,
        },
      });

      if (error) {
        setSaving('');
        // @ts-expect-error - TS2339: Property 'default' does not exist on type '{ readonly defaultAPIError: "Somethin...
        ToastMessage.error(error.message || ErrorMessageConstant.default);
      } else {
        const res = await WalletService.add({
          payment_method_id: paymentMethod.id,
          payment_method_type: 'card',
        });

        if (res?.data?.status === 'requires_action') {
          await stripe.confirmCardSetup(res.data.client_secret);
          await WalletService.makeDefault({
            payment_method_id: paymentMethod.id,
            payment_method_type: 'card',
          });
        }

        close(true);
      }
    } catch (error) {
      // @ts-expect-error - TS2339: Property 'default' does not exist on type '{ readonly defaultAPIError: "Somethin...
      ToastMessage.error(error.error || ErrorMessageConstant.default);
    } finally {
      setSaving('');
    }
  };

  return (
    <>
      {saving && <LoaderBar content={saving} />}
      <form onSubmit={handleSubmit(addCard)}>
        <div className="sk:mb-4 sk:flex sk:items-center sk:justify-between sk:gap-3">
          <span className={HEADING}>Add a Credit Card</span>
          <AppModalXIcon onClick={() => close()} />
        </div>
        <ControlledTextFormField
          error={errors.name}
          name="name"
          placeholder="Cardholder Name"
          label="Name on Card"
          control={control}
          required
        />
        <div className="sk:mb-4 sk:flex sk:flex-col sk:gap-1">
          <FieldRequiredLabel>Card Number</FieldRequiredLabel>
          <div className={STRIPE_FIELD}>
            <CardNumberElement
              options={{ ...options, showIcon: true }}
              onChange={e => setCardNumberCompleted(!!e?.complete)}
            />
          </div>
        </div>
        <div className="sk:mb-4 sk:grid sk:grid-cols-2 sk:gap-3">
          <div className="sk:flex sk:flex-col sk:gap-1">
            <FieldRequiredLabel>Expiration Date</FieldRequiredLabel>
            <div className={STRIPE_FIELD}>
              <CardExpiryElement
                options={options}
                onChange={e => setCardExpiryCompleted(!!e?.complete)}
              />
            </div>
          </div>
          <div className="sk:flex sk:flex-col sk:gap-1">
            <FieldRequiredLabel>Security Code</FieldRequiredLabel>
            <div className={STRIPE_FIELD}>
              <CardCvcElement
                options={options}
                onChange={e => setCardCVCCompleted(!!e?.complete)}
              />
            </div>
          </div>
        </div>
        <StyledButton
          className="sk:flex! sk:justify-center"
          disabled={
            !completedCardNumber || !completedCardExpiry || !completedCardCVC
          }
          fluid
        >
          Add Credit Card
        </StyledButton>
      </form>
    </>
  );
}

function AddCardConfirmation({ close, add }) {
  return (
    <>
      <div className="sk:flex sk:items-start sk:justify-between sk:gap-3">
        <span className={HEADING}>
          Please Add a Credit Card to Complete This Task
        </span>
        <AppModalXIcon onClick={() => close()} />
      </div>
      <div className="sk:mt-3 sk:flex sk:gap-2">
        <BorderButton type="button" onClick={() => close()}>
          Go Back
        </BorderButton>
        <StyledButton
          onClick={() => add()}
          className="sk:flex! sk:items-center sk:justify-center"
        >
          <Plus className="sk:mr-1" /> Credit Card
        </StyledButton>
      </div>
    </>
  );
}

function SuccessBox({ close }) {
  return (
    <div className="sk:flex sk:flex-col sk:items-center sk:text-center">
      <div className="sk:flex sk:w-full sk:justify-end">
        <AppModalXIcon onClick={() => close()} />
      </div>
      <img
        src="/images/home/checkmark.svg"
        alt=""
        className="sk:my-6 sk:size-[70px]"
      />
      <span className={HEADING}>
        The credit card has been successfully added to your wallet.
      </span>
      <MuteText className="sk:mt-3 sk:mb-2">
        Your wallet can be accessed through your workspace settings.
      </MuteText>
    </div>
  );
}

export function AddCreditCardPopup({ open, closeModal }) {
  const [view, setView] = useState(AddCreditCardView.addCreditCard);

  const closeAddCard = value => {
    if (value) {
      setView(AddCreditCardView.success);
    } else {
      closeModal();
      setView(AddCreditCardView.addCreditCard);
    }
  };

  return (
    // @ts-expect-error - ts-migration
    <Elements stripe={getStripe()} options={elementsOptions}>
      <PaymentDialog
        open={open}
        onClose={() => closeModal()}
        aria-label="Add a Credit Card"
      >
        {view === AddCreditCardView.message ? (
          <AddCardConfirmation
            close={() => closeModal()}
            add={() => setView(AddCreditCardView.addCreditCard)}
          />
        ) : view === AddCreditCardView.addCreditCard ? (
          <AddCreditCard close={closeAddCard} />
        ) : (
          <SuccessBox close={() => closeModal(true)} />
        )}
      </PaymentDialog>
    </Elements>
  );
}
